import { useEffect, useRef, useState, useContext } from 'react';
import StarSvg from 'public/images/svg_icons/star.svg';
import { Modal, Pagination } from 'react-bootstrap';
import {
  Option,
  Image,
  OptionHighlight,
  Tag,
  DetailedTimeslot,
  AvailableDate,
  EnumOptionKind,
  EnumOptionAccommodationSaleType,
  SUPPORTED_LOCALES,
} from 'src/types';
import { FiX } from 'react-icons/fi';
import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import Link from 'next/link';
import useQuery from 'src/hooks/useQuery';
import ImageWithFallback from './ImageWithFallback';
import CheckInCircleSvg from 'public/images/svg_icons/check_in_circle.svg';
import Loading from './Loading';
import Slider from 'react-slick';
import { commonCarouselSettings, DBDateFormat, LOCALE_DATE_FORMAT } from 'src/lib/constants';
import EmbeddedVideo from 'src/components/EmbeddedVideo';
import Player from 'src/lib/video_player';
import useBreakpoints from 'src/hooks/useBreakpoints';
import HeartButton from 'src/components/HeartButton';
import HeartBorderSvg from 'public/images/svg_icons/heart_border.svg';
import OptionCardPrice from './OptionCardPrice';
import useBucketList from 'src/hooks/useBucketList';
import dayjs from 'dayjs';
import { groupBy, sumBy } from 'lodash-es';
import pluralize from 'pluralize';
import useSearchBarState from 'src/hooks/useSearchBarState';
import { YoutubeIframeApiContext } from 'src/contexts/YoutubeIframeApiProvider';
import { isYoutubeVideo } from 'src/helpers';
import { useLocale, useTranslations } from 'next-intl';

type Props = {
  option: Option;
  show: boolean;
  isGift?: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
};

const OptionQuickViewModal: React.FC<Props> = ({ show, isGift, setShow, option }) => {
  const { isReady: isYoutubeReady } = useContext(YoutubeIframeApiContext)
  const { bucketListOptions } = useBucketList();
  const locale = useLocale();
  const translation = useTranslations('components.OptionQuickViewModal')
  const { isScreenMd } = useBreakpoints();
  const UIDateFormat = LOCALE_DATE_FORMAT[locale].UIDateFormat
  const [searchBarParams] = useSearchBarState();
  const isSearchDate = searchBarParams.dates.length === 2;
  const { data: fetchedOption, isLoading } = useQuery<Option>(
    ApiRoutes.apiOptionRoute({ id: option.id }, { view: 'quick_view' }).toUrl()
  );

  const { data, isLoading: isLoadingAvailability } = useQuery<{
    detailedTimeslots: DetailedTimeslot[];
    availableDates: AvailableDate[];
    pagination: Pagination;
  }>(
    ApiRoutes.apiOptionAvailabilityRoute(
      {
        id: option.slugCode,
      },
      {
        perPage: 20,
        maxDate: isSearchDate ? searchBarParams.dates[1] : null,
        minDate: isSearchDate ? searchBarParams.dates[0] : null,
      }
    ).toUrl()
  );
  const { detailedTimeslots = [], availableDates = [] } = data || {};
  const groupedTimeslots = groupBy(detailedTimeslots, (item) =>
    dayjs(item.date).format(DBDateFormat)
  );
  const groupedAvailableDates = groupBy(availableDates, (item) =>
    dayjs(item.date).format(DBDateFormat)
  );
  const groupedAvailabilitySlots =
    option.kind === EnumOptionKind.shortAdventure ? groupedTimeslots : groupedAvailableDates;

  const optionName = `${isGift ? `[${translation('gift')}] ` : ''}${option.name}`;
  const [highlights, setHighlights] = useState<OptionHighlight[]>([]);
  const [suitableTags, setSuitableTags] = useState<Tag[]>([]);
  const [mediaAssets, setMediaAssets] = useState<(string | Image)[]>([...option.images]);
  const mainSlider = useRef<Slider>(null);
  const navSlider = useRef<Slider>(null);
  const pauseCurrentVideo = () => {
    if (mainSlider?.current?.innerSlider?.list) {
      const activeVideo = mainSlider.current.innerSlider.list.querySelector(
        '.slick-active.slick-current .js-video-embed'
      );
      const videos = mainSlider.current.innerSlider.list.querySelectorAll(
        '.slick-slide .js-video-embed'
      );
      Array.from(videos)
        .filter((item) => item !== activeVideo)
        .map((iframe) => {
          if (iframe instanceof HTMLIFrameElement) {
            new Player(iframe, {
              onReady: (player) => {
                player.pause()
              }
            });
          }
        });

      if (activeVideo instanceof HTMLIFrameElement) {
        new Player(activeVideo, {
          onReady: (player) => {
            player.play();
            player.setMuted(false);
          }
        });
      }
    }
  };

  useEffect(() => {
    if (fetchedOption) {
      const videoAssets =
        fetchedOption.youtubeIds.length > 0
          ? fetchedOption.youtubeIds
          : [];
      setHighlights(fetchedOption.highlights || []);
      setSuitableTags(fetchedOption.suitableTags || []);
      setMediaAssets([...videoAssets, ...option.images]);
    }
  }, [fetchedOption?.id]);

  return isScreenMd ? (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      dialogClassName="modal-xl modal-dialog-centered modal-dialog-scrollable modal-full-width--sp"
    >
      <Modal.Header>
        <button type="button" className="close" onClick={() => setShow(false)}>
          <FiX size={24} className="tw-text-brand-navy" />
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className="tw-flex tw--mx-4">
          <div className="tw-w-1/2 tw-relative tw-px-4">
            <div className="tw-sticky tw-top-0 tw-left-0 tw-relative">
              <Slider
                {...commonCarouselSettings}
                arrows={true}
                dots={false}
                slidesToShow={1}
                slidesToScroll={1}
                asNavFor={navSlider.current as Slider}
                ref={mainSlider}
                afterChange={(_) => {
                  pauseCurrentVideo();
                }}
                className="carousel-option-quick-view-images tw-mx-0 tw-mb-8 md:tw-mb-4"
              >
                {mediaAssets.map((item) => (
                  <div
                    key={(item as Image).id}
                    className="carousel-option-quick-view-images__item-img"
                  >
                    {typeof item === 'string' ? (
                      <div className="lightbox__main-carousel-item-img tw-h-full">
                        <div className="lightbox-video-container">
                          <EmbeddedVideo videoConfig={{ controls: 1 }} videoId={item} />
                        </div>
                      </div>
                    ) : (
                      <ImageWithFallback
                        className="tw-object-cover tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg"
                        fill
                        record={item as Image}
                        imgField="webpMdUrl"
                        alt={(item as Image).caption || optionName}
                      />
                    )}
                  </div>
                ))}
              </Slider>

              <div>
                <Slider
                  ref={navSlider}
                  asNavFor={mainSlider.current as Slider}
                  slidesToShow={3.2}
                  slidesToScroll={1}
                  focusOnSelect={true}
                  swipeToSlide={true}
                  infinite={false}
                  arrows={false}
                >
                  {mediaAssets.map((item, index) => {
                    const isYoutubeId = isYoutubeVideo(String(item))
                    return (
                      <div key={index} className="tw-px-1 tw-cursor-pointer">
                        <div className="lightbox__nav-carousel-item-img">
                          {typeof item === 'string' && !isYoutubeId ? (
                            <div className="position-relative">
                              <EmbeddedVideo videoId={item} />
                              <div className="video-overlay"></div>
                            </div>
                          ) : (
                            <ImageWithFallback
                              record={
                                (isYoutubeId
                                  ? { webpMdUrl: `https://img.youtube.com/vi/${item}/hqdefault.jpg` }
                                  : item) as Image
                              }
                              fill
                              className="tw-object-cover tw-static tw-block tw-rounded-md"
                              alt={(item as Image).caption || optionName}
                              imgField="webpMdUrl"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Slider>
              </div>
            </div>
          </div>
          <div className="tw-w-1/2 tw-px-4">
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
              <div className="tw-font-styleSans tw-font-semibold tw-text-4xl tw-leading-none md:tw-leading-none tw-mr-2">
                {optionName}
              </div>
              <HeartButton
                isGiftExperience={isGift ? true : false}
                className="tw-cursor-pointer tw-transition-transform tw-duration-500 tw-ease-in-out tw-transform hover:tw-scale-110"
                bucketListOption={bucketListOptions.find(
                  (o) =>
                    o.supplierActivityOptionId === option.id &&
                    o.isGiftExperience === (isGift ? true : false)
                )}
                option={option}
                heartBlankIcon={
                  <HeartBorderSvg className="tw-align-top tw-h-6 tw-w-6 tw-text-black tw-text-opacity-30" />
                }
                heartFillIcon={
                  <HeartBorderSvg className="tw-align-top tw-h-6 tw-w-6 tw-text-orange" />
                }
              />
            </div>
            <div className="tw-mb-5">
              {option.ratedReviewsCount > 0 && (
                <>
                  <StarSvg width={16} height={16} className="tw-text-orange tw-mb-1" />
                  <span className="tw-mx-1">{option.reviewRating}</span>
                  <span>({option.ratedReviewsCount})</span>
                  <span className="tw-mx-1.5">|</span>
                </>
              )}
              <span> {option.isPrivateTrip ? translation('privateTrip') : translation('joinInTrip')}</span>
              {Number(option.maxBookingQuantity) > 0 && (
                <span>
                  <span className="tw-mx-1.5">|</span>
                  <span>{translation('upToGuest', {maxQuantity: option.maxBookingQuantity})}</span>
                </span>
              )}

              <div className="tw-text-lg tw-text-brand-navy tw-cursor-pointer tw-mt-4">
                <OptionCardPrice
                  option={option}
                  isGift={isGift}
                />
              </div>
            </div>

            {isLoading ? (
              <Loading className="tw-justify-center tw-mt-5" />
            ) : fetchedOption ? (
              <div>
                {fetchedOption?.uspHtml ? (
                  <>
                    <div className="tw-border-t tw-border-grey-lighter tw-my-4 md:tw-my-6"></div>
                    <div>
                      <div className="tw-font-semibold tw-mb-5">{translation('whyThisIsDifferent')}</div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: fetchedOption.uspHtml || '',
                        }}
                      ></div>
                    </div>
                  </>
                ) : null}

                {highlights && highlights.length > 0 ? (
                  <>
                    <div className="tw-border-t tw-border-grey-lighter tw-my-4 md:tw-my-6"></div>
                    <div>
                      <div className="tw-font-semibold tw-mb-5">{translation('highlights')}</div>
                      {highlights.map((highlight) => (
                        <div key={highlight.id} className="tw-mb-4 tw-flex tw-items-center">
                          <ImageWithFallback
                            record={highlight.images[0]}
                            imgField="webpMdUrl"
                            alt={highlight.images[0]?.caption || ''}
                            className="tw-static tw-object-cover tw-w-20 tw-h-15 tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg tw-mr-4"
                            fill
                          />
                          <div>{highlight.body}</div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}

            {option.inclusions.length > 0 ? (
              <>
                <div className="tw-border-t tw-border-grey-lighter tw-my-4 md:tw-my-6"></div>
                <div>
                  <div className="tw-font-semibold tw-mb-5">{translation('whatIsIncluded')}</div>
                  {option.inclusions.map((text, index) => (
                    <div key={index} className="tw-mb-2">
                      <div className="tw-flex tw-items-center">
                        <CheckInCircleSvg className="tw-h-5 tw-w-5 tw-text-brand-navy tw-mr-2" />
                        <span>{text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {isLoading ? (
              <Loading className="tw-justify-center tw-mt-5" />
            ) : suitableTags && suitableTags.length > 0 ? (
              <>
                <div className="tw-border-t tw-border-grey-lighter tw-my-4 md:tw-my-6"></div>
                <div>
                  <div className="tw-font-semibold tw-mb-5">{translation('suitabilityDietaryInfo')}</div>
                  {suitableTags.map((tag, index) => (
                    <div key={index} className="tw-mb-2">
                      <div className="tw-flex tw-items-center">
                        <CheckInCircleSvg className="tw-h-5 tw-w-5 tw-text-brand-navy tw-mr-2" />
                        <span>{tag.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {isLoadingAvailability ? (
              <Loading className="tw-justify-center tw-mt-5" />
            ) : Object.keys(groupedAvailabilitySlots).length > 0 ? (
              <div>
                <div className="tw-border-t tw-border-grey-lighter tw-my-4 md:tw-my-6"></div>
                <div className="tw-font-semibold tw-mb-5">{translation('nextAvailableDates')}</div>

                {Object.keys(groupedAvailabilitySlots)
                  .sort()
                  .slice(0, isSearchDate ? undefined : 5)
                  .map((date) => {
                    let totalCapacity = 0;
                    let capacityText = '';
                    if (option.kind === EnumOptionKind.shortAdventure) {
                      totalCapacity = sumBy(groupedTimeslots[date], (o) => o.capacity);
                      capacityText = `${totalCapacity <= 3 ? `${translation('only')} ` : ''}${translation('pluralSpotLeft', {count: totalCapacity})}`;
                    } else {
                      totalCapacity = sumBy(groupedAvailableDates[date], (o) => o.capacity);
                      capacityText = `${totalCapacity <= 3 ? `${translation('only')} ` : ''}${
                        option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom
                          ? translation('pluralRoomLeft', { count: totalCapacity })
                          : translation('pluralSpotLeft', { count: totalCapacity })
                      }`;
                    }

                    return (
                      <div key={date} className="tw-flex tw-items-center tw-mb-3">
                        <div className="tw-w-32">{dayjs(date).format(UIDateFormat)}</div>
                        {totalCapacity > 0 && !option.isPrivateTrip ? (
                          <div>{capacityText}</div>
                        ) : null}
                      </div>
                    );
                  })}
              </div>
            ) : null}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn btn-outline tw-bg-white md:tw-text-base tw-text-sm tw-leading-none md:tw-leading-none"
          onClick={() => {
            setShow(false);
          }}
        >
          {translation('close')}
        </button>
        <Link
          legacyBehavior
          prefetch={false}
          href={AppRoutes.experienceRoute(
            { id: option.slugCode },
            isGift ? { gift_experience: 1 } : {}
          ).toPath()}
        >
          <a className="btn btn-primary">{translation('seeFullDetails')}</a>
        </Link>
      </Modal.Footer>
    </Modal>
  ) : null;
};

export default OptionQuickViewModal;
